import { type RefObject, useState, useEffect, useCallback } from 'react';

import { useResizeObserver } from '@cognite/cogs-utils-v11/hooks/use-resize-observer/use-resize-observer.tsx';

// How this works:
// - scrollableParent: a parent element that can overflow
// - rightMostElement: a child element that is the right most in
//   scrollableParent i.e. which is the one that goes out of the parent's
//   boundaries. This could either be elementToTruncate itself, or if there are
//   further elements to the right of elementToTruncate, it can be it's
//   parent/grandparent that is the right-most child of scrollableParent
// - elementToTruncate: The element which should be shrunk via maxWidth so that
//   the overall width of all the children inside scrollableParent remains equal
//   to it's own width and overflowing doesn't occur
// - 1. we take original widths of rightMostElement and elementToTruncate on
//      first render
// - 2. we then calculate how much of rightMostElement's right coordinate exceeds
//      out of scrollableParent's right coordinate. this can happen anytime
//      scrollableParent's width changes either via editing it's children or
//      viewport resizing
// - 3. we then subtract this offset from the original width of
//      elementToTruncate to get our maxWidth that we can use on the element
// ---- so in the end we shrink the elementToTruncate by how much the
//      rightMostElement went outside the scrollableParent

export const useResponsiveMaxWidth = (
  scrollableParentRef: RefObject<HTMLElement>,
  rightMostElementRef: RefObject<HTMLElement>,
  elementToTruncateRef: RefObject<HTMLElement>,
  scrollPreventionOffset = 12
): string | undefined => {
  const [rightMostElementOriginalWidth, setRightMostElementOriginalWidth] =
    useState<number | undefined>(undefined);
  const [elementToTruncateOriginalWidth, setElementToTruncateOriginalWidth] =
    useState<number | undefined>(undefined);
  const [elementToTruncateMaxWidth, setElementToTruncateMaxWidth] = useState<
    string | undefined
  >(undefined);

  // to trigger offset calculation every time the scrollable parent changes size
  const scrollableParentSize = useResizeObserver(scrollableParentRef);

  // calculates the original width from which the offset is subtracted later
  useEffect(() => {
    setRightMostElementOriginalWidth(rightMostElementRef.current?.clientWidth);
    setElementToTruncateOriginalWidth(
      elementToTruncateRef.current?.clientWidth
    );
  }, [elementToTruncateRef, rightMostElementRef]);

  // calculates the offset by measuring how much the right most element is
  // outside the bounds of the parent container
  const calculateOffset = useCallback(() => {
    if (
      !scrollableParentRef.current ||
      !rightMostElementRef.current ||
      !rightMostElementOriginalWidth
    )
      return undefined;

    const scrollableParentRightBoundary =
      scrollableParentRef.current.getBoundingClientRect().left +
      scrollableParentRef.current.clientWidth;

    const rightMostElementOriginalRightBoundary =
      rightMostElementRef.current.getBoundingClientRect().left +
      rightMostElementOriginalWidth;

    if (rightMostElementOriginalRightBoundary > scrollableParentRightBoundary)
      return (
        rightMostElementOriginalRightBoundary - scrollableParentRightBoundary
      );

    return undefined;
  }, [scrollableParentRef, rightMostElementOriginalWidth, rightMostElementRef]);

  // calculates max width by subtracting offset from the original width that was
  // calculated after first render
  useEffect(() => {
    const offset = calculateOffset();
    if (elementToTruncateOriginalWidth && offset)
      setElementToTruncateMaxWidth(
        `${Math.max(0, elementToTruncateOriginalWidth - offset - scrollPreventionOffset)}px`
      );
    else setElementToTruncateMaxWidth(undefined);
  }, [
    scrollableParentSize,
    elementToTruncateOriginalWidth,
    rightMostElementOriginalWidth,
    rightMostElementRef,
    calculateOffset,
    scrollPreventionOffset,
  ]);

  return elementToTruncateMaxWidth;
};
